/**
 * Contenedor de pantalla de Staff (design-system §8.1): fondo Crema, `min-height: 100dvh`,
 * safe areas (`env(safe-area-inset-*)`, utilidades `fm-safe-*` de `styles/index.css`), gutter
 * lateral de 16 px y tres slots:
 *
 * - `header`: arriba (p. ej. el `Stepper` de activación).
 * - contenido: `title` (Nunito 800 28 px), `lead` (16 px secundario) y `children`.
 * - `footer`: acciones inferiores fijas (el botón primario de 56 px), pegadas al borde inferior y
 *   con el inset sumado a su padding, como manda §8.1 para la barra inferior.
 * - `nav` (006 · T016, research R-2): la barra de pestañas (`TabBar`), bajo el `footer`. Con `nav`
 *   el `footer` **no** suma el inset inferior: lo suma la barra, que queda pegada al borde. Solo
 *   lo pasan Inicio, Asistencia, Perfil y el aviso de privacidad.
 *
 * Nota T056: no actualiza `document.title` por pantalla — `tests/e2e/smoke.spec.ts` fija
 * `toHaveTitle('Fermin')` como invariante del shell y es un archivo prohibido para este lote; un
 * título dinámico por pantalla queda pendiente de una decisión conjunta con quien mantenga ese E2E.
 */
import { cn } from './cn';

import type { ReactNode } from 'react';

export interface ScreenProps {
  title?: string;
  lead?: string;
  header?: ReactNode;
  footer?: ReactNode;
  /** Barra de pestañas (`<TabBar active=… />`); con ella el footer no suma el inset inferior. */
  nav?: ReactNode;
  children?: ReactNode;
  /** `center` centra verticalmente el contenido (pantallas de estado y de éxito). */
  align?: 'start' | 'center';
  className?: string;
  contentClassName?: string;
  'data-testid'?: string;
}

export function Screen({
  title,
  lead,
  header,
  footer,
  nav,
  children,
  align = 'start',
  className,
  contentClassName,
  'data-testid': testId,
}: ScreenProps) {
  return (
    <div
      data-testid={testId}
      data-fm-screen=""
      className={cn(
        'fm-safe-top fm-safe-x flex min-h-dvh flex-col bg-fm-bg text-fm-text',
        className,
      )}
    >
      {header ? <header className="px-fm-4 pt-fm-4">{header}</header> : null}

      <main
        className={cn(
          'flex flex-1 flex-col gap-fm-4 px-fm-4 py-fm-6',
          align === 'center' && 'justify-center',
          contentClassName,
        )}
      >
        {title ? <ScreenTitle>{title}</ScreenTitle> : null}
        {lead ? <ScreenLead>{lead}</ScreenLead> : null}
        {children}
      </main>

      {nav ? (
        <div data-screen-bottom="" className="sticky bottom-0 bg-fm-bg">
          {footer ? (
            <div className="flex flex-col gap-fm-3 px-fm-4 pt-fm-3 pb-fm-4">{footer}</div>
          ) : null}
          {nav}
        </div>
      ) : footer ? (
        <div
          data-screen-footer=""
          className="fm-safe-bottom-gutter sticky bottom-0 flex flex-col gap-fm-3 bg-fm-bg px-fm-4 pt-fm-3"
        >
          {footer}
        </div>
      ) : (
        <div aria-hidden="true" className="fm-safe-bottom" />
      )}
    </div>
  );
}

export function ScreenTitle({ className, children }: { className?: string; children: ReactNode }) {
  return (
    <h1 className={cn('font-display text-fm-2xl font-extrabold text-fm-text', className)}>
      {children}
    </h1>
  );
}

export function ScreenLead({ className, children }: { className?: string; children: ReactNode }) {
  return <p className={cn('text-fm-base text-fm-text-secondary', className)}>{children}</p>;
}
